<script>
	import LoadingIndicator from '@Components/LoadingIndicator.svelte'
	import { onMount, getContext, onDestroy } from 'svelte'
	import Service from '@Renderer/Service.js'
	const { getController, getEditorCanvas, getConfig, getRenderer, getEditorState } = getContext('editor')
	import { deconstructHoleId } from '@Utils/DataController.js'
	let { visible = $bindable(false) } = $props()
	import { getIconHtml } from '@Utils/Icons.js'
	import { getRenderProperties } from '@Utils/Properties.js'
	import { GreenRenderer } from '@GreenEditor/GreenRenderer.js'
	import { GreenEditor } from '@GreenEditor/GreenEditor.js'
	import { convertToBlob, convertCanvasToBlob } from '@Utils/Canvas.js'
	import { sleep } from '@Utils/Runtime.js'

	let controller = getController()
	let subscription = controller.getSubscription()
	let holeRenderer = getRenderer()

	let config = getConfig()
	let editorCanvas = getEditorCanvas()
	let editorState = getEditorState()
	let zoomCanvas = $state()
	let objects = $state([])
	let status = $state('idle')
	let isDarkMode = $state(window.isDarkMode)
	let renderButtonDisabled = $state(true)
	let cancelled = $state(false)
	let resetButtonDisabled = $state(false)
	let currentStatus = $state('')
	const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
	const isChrome = window.chrome;
	let holeRatio = import.meta.env.VITE_RENDER_WIDTH / import.meta.env.VITE_RENDER_HEIGHT
	let courseRatio = import.meta.env.VITE_RENDER_HEIGHT / import.meta.env.VITE_RENDER_WIDTH
	let isSupported = $state(false)
	if (typeof OffscreenCanvas !== 'undefined' && (isChrome || isFirefox)) {
		isSupported = true
	}

	function setupEventListeners() {
		window.addEventListener('colorSchemeChanged', (e) => {
			isDarkMode = e.isDarkMode
		})
		holeRenderer.on('status', function (status) {
			if (status == 'Done') {
				currentStatus = ''
			} else {
				currentStatus = status
			}
		})
	}

	function setStatus(s) {
		status = s
		if (status !== 'preparing' && status !== 'rendering') {
			resetButtonDisabled = false
		} else {
			resetButtonDisabled = true
		}
		if (status === 'waiting') {
			renderButtonDisabled = false
		} else {
			renderButtonDisabled = true
		}
	}
	function getColor(status) {
		if (status == 'error') {
			return '#F9423A'
		}
		if (status == 'waiting') {
			return '#E48B23'
		}
		if (status == 'rendering') {
			return '#B1B5CE'
		}
		if (status == 'saving') {
			return '#B1B5CE'
		}
		if (status == 'done') {
			return '#26D07C'
		}
		if (status == 'invalid') {
			return '#9c8e7f'
		}
	}
	function close() {
		if (status === 'rendering') {
			if (confirm('Are you sure you want to cancel the rendering?')) {
				cancelRendering()
				visible = false
			}
		} else {
			visible = false
		}
	}
	function triggerUpdate() {
		objects = [...objects]
	}
	function isLoading(status) {
		return status !== 'waiting' && status !== 'error' && status !== 'done' && status !== 'invalid'
	}
	function isRendering(status) {
		return status === 'rendering'
	}
	async function renderCourse(object) {
		let data = editorCanvas.getCourseRenderData(object.course.courseId)
		if (!data) {
			throw 'error'
		}
		let courseRenderProperties = getRenderProperties({
			backgroundColor: '#ffffff',
			height: Math.round(import.meta.env.VITE_RENDER_WIDTH * 1.5),
			includeTexts: true,
			includeHud: true,
			includeIcons: true,
			includePlaylines: true,
			includeTeeColors: false,
			includeTeeMarkers: false,
			
			largeForest: true,
			renderTarget: 'course',
			shadowIntensity: 1.1,
			shadowMapSize: 2048,
			saveData: false,
			texturePack: object.course.theme ?? 'park',
			width: Math.round(import.meta.env.VITE_RENDER_HEIGHT * 1.5)
		})
		let result = await holeRenderer.render(data, courseRenderProperties)
		if (!result || !result.image || !result.data) {
			throw 'error'
		}
		if (!visible) {
			throw 'cancelled'
		}
		object.renderingResult = result
		const blob = await convertToBlob({
			image: result.image,
			type: import.meta.env.VITE_RENDER_IMAGE_TYPE ?? 'image/jpeg',
			quality: import.meta.env.VITE_RENDER_IMAGE_QUALITY ?? 0.7
		})
		if (!visible) {
			throw 'cancelled'
		}
		if (!visible) {
			throw 'cancelled'
		}
		object.blob = blob
		await assignImage(blob, object.element)
	}
	async function renderHole(object) {
		let data = editorCanvas.getHoleRenderData(object.hole.holeId)
		if (!data) {
			throw 'error'
		}
		let theme = controller.getGroupSetting(object.hole.holeGroupId, 'theme')
		let holeRenderProperties = getRenderProperties({
			width: import.meta.env.VITE_RENDER_WIDTH,
			height: import.meta.env.VITE_RENDER_HEIGHT,
			texturePack: theme ?? 'park',
			renderTarget: 'hole',
			backgroundColor: '#ffffff',
			shadowIntensity: 0.9,
			shadowMapSize: 1024,
			includeIcons: true,
			includeTeeMarkers: false,
			includeTeeColors: false,
			includePlaylines: false,
			saveData: false,
			inclideTexts: true,
			includeHud: true
		})
		let result = await holeRenderer.render(data, holeRenderProperties)
		if (!result || !result.image || !result.data) {
			throw 'error'
		}
		if (!visible) {
			throw 'cancelled'
		}
		if (['premium'].includes(subscription)) {
			let greenInclinationCanvas = document.createElement('canvas')
			greenInclinationCanvas.width = import.meta.env.VITE_RENDER_WIDTH
			greenInclinationCanvas.height = import.meta.env.VITE_RENDER_WIDTH

			let greenZonesCanvas = document.createElement('canvas')
			greenZonesCanvas.width = import.meta.env.VITE_RENDER_WIDTH
			greenZonesCanvas.height = import.meta.env.VITE_RENDER_WIDTH

			let greenData = editorCanvas.getGreenRenderData(object.hole.holeId)
			if (greenData) {
				let greenRenderProperties = getRenderProperties({
					width: import.meta.env.VITE_RENDER_WIDTH,
					height: import.meta.env.VITE_RENDER_WIDTH,
					texturePack: theme ?? 'park',
					backgroundColor: '#ffffff',
					renderTarget: 'green',
					shadowIntensity: 0.9,
					shadowMapSize: 512,
					includeIcons: false,
					includeTexts: true,
					saveData: false,
					includeTeeMarkers: false,
					includeTeeColors: false,
					includePlaylines: false,
					includeHud: true,
				})
				let greenResult = await holeRenderer.render(greenData, greenRenderProperties)
				if (!greenResult || !greenResult.image || !greenResult.data) {
					throw 'error'
				}
				
				greenInclinationCanvas.getContext('2d').clearRect(0, 0, greenInclinationCanvas.width, greenInclinationCanvas.height)
				greenZonesCanvas.getContext('2d').clearRect(0, 0, greenZonesCanvas.width, greenZonesCanvas.height)
				greenInclinationCanvas.getContext('2d').drawImage(greenResult.image, 0, 0, greenInclinationCanvas.width, greenInclinationCanvas.height)
				greenZonesCanvas.getContext('2d').drawImage(greenResult.image, 0, 0, greenZonesCanvas.width, greenZonesCanvas.height)
				sleep(100)
				let holeGreenData = controller.getGreenData(object.hole.holeId)
				if (holeGreenData) {
					let options = {
						pixelsPerMeter: greenResult.data.boundingBox.pixelsPerMeter,
						clear: false
					}
					let inclinationRenderer = new GreenRenderer(greenInclinationCanvas, greenInclinationCanvas.getContext('2d'))
					let d = holeGreenData.filter((d) => d.type === 'Line' && d.properties && d.properties.inclination === true)
					inclinationRenderer.render(GreenEditor.readObjects(d), options)

					let zonesRenderer = new GreenRenderer(greenZonesCanvas, greenZonesCanvas.getContext('2d'))
					d = holeGreenData.filter((d) => (d.type === 'Line' && d.properties && d.properties.inclination !== true) || d.type !== 'Line')
					zonesRenderer.render(GreenEditor.readObjects(d), options)
				}
				sleep(100)
				const options = {
					type: import.meta.env.VITE_RENDER_IMAGE_TYPE ?? 'image/jpeg',
					quality: import.meta.env.VITE_RENDER_IMAGE_QUALITY ?? 0.7
				}
				const greenInclinationBlob = await convertCanvasToBlob(greenInclinationCanvas, options, 'inclination')
				const greenZonesBlob = await convertCanvasToBlob(greenZonesCanvas, options, 'zones')
				await assignImage(greenInclinationBlob, object.greenInclinationElement)
				await assignImage(greenInclinationBlob, object.greenZonesElement)
				object.greenInclinationBlob = greenInclinationBlob
				object.greenZonesBlob = greenZonesBlob
			} else {
				console.log('No green data found')
			}
		}
		object.renderingResult = result
		const holeBlob = await convertToBlob(
			{
				image: result.image,
				type: import.meta.env.VITE_RENDER_IMAGE_TYPE ?? 'image/jpeg',
				quality: import.meta.env.VITE_RENDER_IMAGE_QUALITY ?? 0.7
			},
			'holeimage'
		)
		object.holeImageBlob = holeBlob
		if (!visible) {
			throw 'cancelled'
		}
		await assignImage(holeBlob, object.element)
	}
	async function cancelRendering() {
		holeRenderer.cancel()
		cancelled = true
	}
	async function render(o) {
		setStatus('rendering')
		o.status = 'rendering'
		triggerUpdate()
		try {
			if (o.type === 'course') {
				await renderCourse(o)
			} else {
				await renderHole(o)
			}
			o.status = 'saving'
			o.tooltip = 'Saving to server'
			triggerUpdate()
			if (o.type === 'course') {

				for (let loopId of o.course.loops) {
					await controller.postCourseImage({
						courseId: o.course.courseId,
						image: o.blob,
						imageData: o.renderingResult.data,
						loopId: loopId
					})
				}
				o.image = o.blob
				o.imageData = o.renderingResult.data
				delete o.blob
				delete o.renderingResult
				o.status = 'done'
				o.tooltip = ''
				let feature = editorCanvas.getCourseFrameFeature(o.course.courseId)
				feature.set('imageDate', Date.now())
			} else {
				await controller.postHoleImages({
					holeId: o.hole.holeId,
					holeImage: o.holeImageBlob,
					holeImageData: o.renderingResult.data,
					greenInclinationImage: o.greenInclinationBlob,
					greenZonesImage: o.greenZonesBlob
				})
				delete o.blob
				delete o.renderingResult
				o.status = 'done'
				o.tooltip = ''
				o.hole.hasChange = false
			}
		} catch (e) {
			console.error(e)
			if (e === 'cancelled') {
				o.status = 'waiting'
				o.tooltip = 'Cancelled'
			} else {
				o.status = 'error'
				o.error = e
				let event = new Event('error')
				event.data = {
					type: 'renderError',
					message: 'Error during rendering',
					error: e
				}
				window.dispatchEvent(event)
			}
		}
	}
	async function startRenderingQueue() {
		if (cancelled) {
			cancelled = false
			setStatus('waiting')
			return
		}
		setStatus('rendering')
		let o = objects.find((o) => o.status === 'waiting')
		if (!o) {
			await controller.save()
			await controller.pushChanges(true)
			setStatus('finished')
			return
		}
		await render(o)
		triggerUpdate()
		startRenderingQueue()
	}
	function sortingByName(a, b) {
		const nameA = parseInt(a.displayName, 10) // ignore upper and lowercase
		const nameB = parseInt(b.displayName, 10) // ignore upper and lowercase
		if (nameA < nameB) {
			return -1
		}
		if (nameA > nameB) {
			return 1
		}
		return 0
	}
	async function getImageAsync(url) {
		return new Promise((resolve, reject) => {
			let image = new Image()
			image.crossOrigin = 'anonymous'
			image.onload = () => {
				resolve(image)
			}
			image.onerror = (e) => {
				reject(e)
			}
			image.src = url
		})
	}
	async function assignImage(image, element, url) {
		if(element.tagName === 'CANVAS') {
			if (image && image.type) {
				let bitMap = await createImageBitmap(image)
				await waitForFrame();
				element.getContext('2d').clearRect(0, 0, element.width, element.height)
				element.getContext('2d').drawImage(bitMap, 0, 0, element.width, element.height)
			} else if (url) {
				try {
					let image = await getImageAsync(url)
					await waitForFrame();
					element.getContext('2d').clearRect(0, 0, element.width, element.height)
					element.getContext('2d').drawImage(image, 0, 0, element.width, element.height)
				} catch (err) {
					window.dispatchError({
						type: 'error',
						message: `Error loading image`,
						error: err
					})
				}
			}
		} else if(element.tagName === 'DIV') {
			// remove all children from div and add a new image based on the url or create base64 image based on the blob
			element.innerHTML = ''
			if (image && image.type) {
				let img = document.createElement('img')
				img.classList.add('image-data')
				img.src = URL.createObjectURL(image)
				img.dataset.width = element.dataset.width
				img.dataset.height = element.dataset.height
				element.appendChild(img)
			} else if (url) {
				let img = document.createElement('img')
				img.classList.add('image-data')
				img.src = url
				img.dataset.width = element.dataset.width
				img.dataset.height = element.dataset.height
				element.appendChild(img)
			}
		}
	}
	async function resetStatus(object, save = false) {
		if (object.status === 'done' || object.status === 'waiting') {
			object.status = 'waiting'
			object.tooltip = 'Waiting for render'
		} else {
			return
		}
		objects = [...objects]
		if (objects.find((o) => o.status === 'waiting')) {
			setStatus('waiting')
		} else {
			setStatus('nochanges')
		}
		await render(object)
		if (save) {
			await controller.save()
			await controller.pushChanges(true)
		}
		if (objects.find((o) => o.status === 'waiting')) {
			setStatus('waiting')
		} else {
			setStatus('nochanges')
		}
		triggerUpdate()
	}
	async function waitForFrame() {
		// await new Promise(resolve => {
		// 	requestAnimationFrame(resolve)
		// })
	}
	async function resetCompleted() {
		for (let o of objects) {
			if (o.status === 'done') {
				o.status = 'waiting'
				o.tooltip = 'Waiting for render'
			}
		}
		objects = [...objects]
		if (objects.find((o) => o.status === 'waiting')) {
			setStatus('waiting')
		} else {
			setStatus('nochanges')
		}
	}
	async function redrawAllImages() {
		if(objects && objects.length < 1) {
			return
		}
		for (let o of objects) {
			
			if (o.hole) {
				if (o.hole.image && o.hole.image.type) {
					await assignImage(o.hole.image, o.element)
				} else if (o.hole.holeImageUrl) {
					await assignImage(null, o.element, o.hole.holeImageUrl)
				}
				if (o.hole.greenZonesImage && o.hole.greenZonesImage.type) {
					await assignImage(o.hole.greenZonesImage, o.greenZonesElement)
				} else if (o.hole.greenZonesImageUrl) {
					await assignImage(null, o.greenZonesElement, o.hole.greenZonesImageUrl)
				}
				if (o.hole.greenInclinationImage && o.hole.greenInclinationImage.type) {
					await assignImage(o.hole.greenInclinationImage, o.greenInclinationElement)
				} else if (o.hole.greenInclinationImageUrl) {
					await assignImage(null, o.greenInclinationElement, o.hole.greenInclinationImageUrl)
				}
			} else {
				if (o.image && o.image.type) {
					await assignImage(o.image, o.element)
				} else if (o.course.loops.length > 0) {
					for (let loopId of o.course.loops) {
						let loop = controller.data.loops.find((l) => l.id === loopId)
						if (loop && loop.overviewImageUrl) {
							await assignImage(null, o.element, loop.overviewImageUrl)
							break
						}
					}
				}
			}
		}
	}
	async function prepare() {
		if (status !== 'idle') {
			return
		}

		setStatus('preparing')
		/*await new Promise((r) => setTimeout(r, 10000));*/
		await controller.save()
		await controller.pushChanges(true)
		let arr = []
		let tooltipExtra = '.'
		for (let hole of controller.data.holes.sort(sortingByName)) {
			let status = controller.holeRenderStatus(hole.holeId)

			if (status.status === 'done' || status.status === 'waiting') {
				status.tooltip += tooltipExtra
			}
			let o = {
				hole: hole,
				displayName: deconstructHoleId(hole.holeId).displayName,
				element: null,
				type: 'hole',
				greenInclinationElement: null,
				greenZonesElement: null,
				status: status.status,
				tooltip: status.tooltip
			}
			arr.push(o)
		}
		if (['premium'].includes(subscription)) {
			let courseFrames = editorCanvas.getCourseFrames()
			for (let course of courseFrames.sort(sortingByName)) {
				if (!course.loops || course.loops.length < 1) {
					continue
				}
				let status = controller.courseRenderStatus(course.courseId)
				if (status.status === 'done' || status.status === 'waiting') {
					status.tooltip += tooltipExtra
				}
				let o = {
					course: course,
					displayName: course.displayName,
					element: null,
					type: 'course',
					status: status.status,
					tooltip: status.tooltip
				}
				arr.push(o)
			}
		}
		objects = arr
		if (objects.find((o) => o.status === 'waiting')) {
			setStatus('waiting')
		} else {
			setStatus('nochanges')
		}
		await sleep(100);
		redrawAllImages();
	}
	
	$effect(() => {
		if (visible) {
			prepare()
		}
	})
	
	let mouseOverTarget = null
	let rafId = null
	let cachedContext = null
	
	function updateZoomPosition(clientX, clientY) {
		// place zoom canvas below or above the target depending on the size of the image in relation to the screen
		if (clientX + zoomCanvas.width + 30 > window.innerWidth) {
			zoomCanvas.style.left = clientX - 20 - zoomCanvas.width + 'px'
		} else {
			zoomCanvas.style.left = 20 + clientX + 'px'
		}
		if (clientY + zoomCanvas.height + 30 > window.innerHeight) {
			zoomCanvas.style.top = clientY - 20 - zoomCanvas.height + 'px'
		} else {
			zoomCanvas.style.top = 30 + clientY + 'px'
		}
	}
	
	function mousemove(e) {
		// Throttle with requestAnimationFrame
		if (rafId) {
			return
		}
		
		rafId = requestAnimationFrame(() => {
			rafId = null
			
			if (!cachedContext) {
				cachedContext = zoomCanvas.getContext('2d', {
					willReadFrequently: true
				})
			}
			
			if (e.target && e.target.tagName === 'CANVAS' && e.target.classList.contains('image')) {
				if (mouseOverTarget !== e.target) {
					zoomCanvas.width = e.target.width / 6
					zoomCanvas.height = e.target.height / 6
					cachedContext.imageSmoothingEnabled = true
					cachedContext.imageSmoothingQuality = 'high'
					cachedContext.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height)
					cachedContext.drawImage(e.target, 0, 0, zoomCanvas.width, zoomCanvas.height)
					zoomCanvas.style.visibility = 'visible'
					zoomCanvas.style.opacity = 1
					mouseOverTarget = e.target
				}
				updateZoomPosition(e.clientX, e.clientY)
			} else if (e.target && e.target.tagName === 'IMG' && e.target.classList.contains('image-data')) {
				if (mouseOverTarget !== e.target) {
					zoomCanvas.width = e.target.naturalWidth / 6
					zoomCanvas.height = e.target.naturalHeight / 6
					cachedContext.imageSmoothingEnabled = true
					cachedContext.imageSmoothingQuality = 'high'
					cachedContext.clearRect(0, 0, zoomCanvas.width, zoomCanvas.height)
					cachedContext.drawImage(e.target, 0, 0, zoomCanvas.width, zoomCanvas.height)
					zoomCanvas.style.visibility = 'visible'
					zoomCanvas.style.opacity = 1
					mouseOverTarget = e.target
				}
				updateZoomPosition(e.clientX, e.clientY)
			} else {
				mouseOverTarget = null
				zoomCanvas.style.visibility = 'hidden'
				zoomCanvas.style.opacity = 0
			}
		})
	}
	onMount(() => {
		setupEventListeners()
		if (visible) {
			prepare()
			editorState.setRendererOwner('render-modal')
			document.addEventListener('mousemove', mousemove)
		}
	})
	function oncontextmenu(e) {
		e.preventDefault()
		return false
	}
	onDestroy(() => {
		editorState.setRendererOwner(null)
		document.removeEventListener('mousemove', mousemove)
	})
</script>

{#if visible}
	<canvas bind:this={zoomCanvas} class="box-shadow zoom-canvas pointer-events-none"></canvas>
	<div class="render-progress select-none">
		<div class="overlay box" class:unsupported={!isSupported}>
			<!--LoadingIndicator isLoading={false} hideWhenIdle={true} /-->
			<div class="content">
				<div class="header bg-white dark:bg-bigsteel-700/30">
					<div class="markup select-none" style="width: 50%">
						{#if cancelled}
							<h1>Cancelled!</h1>
							<p><em>Rendering cancelled</em></p>
							<p>Waiting for renderer to respond</p>
						{:else if !isSupported}
							<h1>Unsupported browser</h1>
							<p>We require <a href="https://www.google.com/chrome/">Google chrome</a> or <a href="https://www.microsoft.com/edge">Microsoft Edge</a> to render your images.</p>
							<p>Feel free to go back to golfoffice or continue editing.</p>
						{:else if status === 'finished'}
							<h1>Finished!</h1>
							<p>The mapping tool has completed rendering and saving your holes.</p>
							<p>Feel free to go back to golfoffice or continue editing.</p>
						{:else if status === 'rendering'}
							<h1>Rendering and saving</h1>
							<p>The mapping tool is now rendering your images and saving them to golfoffice.</p>
							<p><em>Do not close your browser</em> or the process will be terminated</p>
						{:else if status === 'waiting' || status === 'preparing'}
							<h1>Rendering your holes</h1>
							<p>When you feel ready you can render images for your holes.</p>
							<p>Remember that it might take some time and if you close your browser before the render has completed you will need to render again.</p>
						{:else if status === 'nochanges'}
							<h1>Nothing to render</h1>
							<p>You don't have any changes to render.</p>
							<p>Feel free to go back to golfoffice or continue editing.</p>
						{/if}
					</div>
					<button class="close-button" onclick={close}>{@html getIconHtml('Close', 'dark:bg-white')}</button>
				</div>
				<div class="center-content">
					{#if isSupported}
						{#if objects.length < 1 && status === 'preparing'}
							<div class="preparing">
								<h1>Preparing</h1>
								<br />
								<LoadingIndicator isLoading={true} color="#E48B23" />
							</div>
						{:else}
							<div class="grid 4xl:grid-cols-7 3xl:grid-cols-6 2xl:grid-cols-5 xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-4 items-grid">
								{#each objects as object}
									<div class="item status-{object.status} bg-white dark:bg-bigsteel-800" class:md:col-span-2={object.type === 'course'}>
										{#if object.type === 'hole'}
											<div class="images grid grid-flow-col grid-rows-2">
												<div role="none" {oncontextmenu} class="image row-span-2" style="height: 90px; aspect-ratio: {holeRatio}" width={import.meta.env.VITE_RENDER_WIDTH} height={import.meta.env.VITE_RENDER_HEIGHT} data-width={import.meta.env.VITE_RENDER_WIDTH} data-height={import.meta.env.VITE_RENDER_HEIGHT} bind:this={object.element}></div>
												{#if ['premium'].includes(subscription)}
													<div role="none" crossorigin="*" {oncontextmenu} class="image row-span-1" style="height: 40px; aspect-ratio: 1" width={import.meta.env.VITE_RENDER_WIDTH} height={import.meta.env.VITE_RENDER_WIDTH} data-width={import.meta.env.VITE_RENDER_WIDTH} data-height={import.meta.env.VITE_RENDER_WIDTH} bind:this={object.greenInclinationElement}></div>
													<div role="none" crossorigin="*" {oncontextmenu} class="image row-span-1" style="height: 40px; aspect-ratio: 1" width={import.meta.env.VITE_RENDER_WIDTH} height={import.meta.env.VITE_RENDER_WIDTH} data-width={import.meta.env.VITE_RENDER_WIDTH} data-height={import.meta.env.VITE_RENDER_WIDTH} bind:this={object.greenZonesElement}></div>
												{/if}
											</div>
										{:else}
											<div class="images grid grid-flow-col grid-rows-2 col-span-3">
												<div role="none" {oncontextmenu} class="image row-span-2" style="height: 90px; aspect-ratio: {courseRatio}" width={import.meta.env.VITE_RENDER_HEIGHT} height={import.meta.env.VITE_RENDER_WIDTH} data-width={import.meta.env.VITE_RENDER_HEIGHT} data-height={import.meta.env.VITE_RENDER_WIDTH} bind:this={object.element}></div>
											</div>
										{/if}

										<div class="item-content">
											<div class="footer">
												<span>Hole {object.displayName}</span>
												<LoadingIndicator isLoading={isLoading(object.status)} color={getColor(object.status)} />
											</div>
											<div class="flex-1"></div>
											<div class="footer" tooltip={object.tooltip} data-status={object.status}>
												{#if object.status === 'rendering'}
													{!currentStatus || currentStatus.length === 0 ? object.status : currentStatus}
												{:else}
													{object.status}
												{/if}
												<button onclick={() => resetStatus(object, true)} disabled={object.status === 'rendering'} tooltip="Click to render">{@html getIconHtml('Render', 'dark:bg-white')}</button>
											</div>
										</div>
									</div>
								{/each}
							</div>
						{/if}
					{/if}
				</div>
				<div class="buttons bg-white dark:bg-bigsteel-700/30">
					<button
						class:btn-solid-white={isDarkMode}
						class:btn-outline-blue={!isDarkMode}
						class="btn btn-sm"
						disabled={isRendering(status)}
						onclick={() => {
							visible = false
						}}>Continue editing</button
					>
					<button
						class:btn-solid-white={isDarkMode}
						class:btn-outline-blue={!isDarkMode}
						class="btn btn-sm"
						disabled={isRendering(status)}
						onclick={() => {
							window.parent.postMessage(
								{
									action: 'closeMap'
								},
								config.golfOfficeFrontendURL
							)
						}}>Back to golfoffice</button
					>
					<button
						class:btn-solid-white={isDarkMode}
						class:btn-outline-blue={!isDarkMode}
						class="btn btn-sm"
						disabled={resetButtonDisabled || !isSupported}
						tooltip="Reset status and re-render all valid holes."
						onclick={() => {
							resetCompleted()
						}}>Mark for rendering</button
					>
					<div class="spacer"></div>
					{#if status === 'rendering' && cancelled == false}
						<button
							class:btn-solid-red={isDarkMode}
							class:btn-outline-red={!isDarkMode}
							class="btn btn-sm"
							tooltip="Cancel the current rendering queue. Completed images has been saved."
							onclick={() => {
								cancelRendering()
							}}>Cancel render</button
						>
					{/if}
					<button
						disabled={renderButtonDisabled || !isSupported}
						class="btn btn-sm btn-solid-blue"
						onclick={() => {
							renderButtonDisabled = true
							cancelled = false
							startRenderingQueue()
						}}>Start rendering</button
					>
				</div>
			</div>
		</div>
	</div>
{/if}

<style lang="postcss">
	.zoom-canvas {
		visibility: hidden;
		position: absolute;
		z-index: 9999;
		background-color: #000000;
		/* transition:visibility 0.1s linear,opacity 0.1s linear; */
		opacity: 0;
	}
	.render-progress {
		font-family: 'Tietoevry sans 1';
		position: absolute;
		z-index: 100;
		top: 0px;
		left: 0px;
		display: flex;
		flex-direction: column;
		flex: 1;
		width: 100vw;
		height: 100vh;
		background-color: rgba(0, 0, 0, 0.5);
		justify-content: center;
		justify-items: center;
	}
	.center-content {
		height: 100%;
		flex: 1;
		overflow-y: auto;
		box-shadow: inset 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
		padding: 20px;
		background-color: rgba(0, 0, 0, 0.1);
	}
	.overlay {
		overflow: hidden;
		display: flex;
		flex-direction: column;
		margin: 40px;
		box-sizing: border-box;
		border-radius: 16px;
		height: 100%;
	}
	.unsupported {
		margin: 40px;
	}
	.header {
		padding: 20px;
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
		align-items: flex-start;
		padding-bottom: 10px;
	}
	.spacer {
		flex: 1;
	}
	.content {
		display: flex;
		flex-direction: column;
		width: 100%;
		height: 100%;
		flex: 1;
		gap: 0px;
		overflow-y: auto;
	}
	.buttons {
		display: flex;
		flex-direction: row;
		gap: 10px;
		justify-content: flex-end;
		padding: 20px;
	}
	.markup {
		flex: 1;
	}
	a {
		color: rgba(24, 97, 242, 1);
		font-weight: 600;
	}
	.items-grid {
	}
	.preparing {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		flex: 1;
	}
	.name {
		font-size: 14px;
		font-weight: 600;
		margin-top: 4px;
		text-align: left;
		width: 100%;
	}
	.item {
		position: relative;
		gap: 10px;
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		border-radius: 8px;
		padding: 10px;
		box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
	}
	.item .images {
		gap: 10px;
	}
	.item .image {
		transition: transform 0.15s;
		transition: background-color 0.15s ease-in-out;
		opacity: 0.5;
		background-color: rgb(228, 139, 35, 0.5);
	}
	.status-done .image {
		opacity: 1;
		background-color: rgba(0, 0, 0, 0.2);
	}
	/* .status-done .image:hover {
		z-index: 9999;
		transform: scale(2);
	} */
	.status-invalid .image {
		background-color: rgb(156, 142, 127, 0.2);
	}
	.status-saving .image {
		background-color: rgb(177, 181, 206, 0.2);
	}
	.item .item-content {
		display: flex;
		justify-content: top;
		flex-direction: column;
		height: 100%;
		width: 100%;
	}
	.item .footer {
		width: 100%;
		display: flex;
		flex-direction: row;
		gap: 10px;
		justify-content: space-between;
		font-size: 14px;
		text-transform: capitalize;
		border-bottom: 1px solid rgba(0, 0, 0, 0.1);
		padding: 5px 0px;
	}
	/* .item .footer[data-status='done'],
	.item .footer[data-status='waiting'] {
		cursor: pointer;
	} */
	.item .footer button {
		visibility: hidden;
	}
	.item .footer button[disabled] {
		opacity: 0.5;
	}
	.item:hover .footer button {
		visibility: visible;
	}
	.item .footer:last-of-type {
		border-bottom: none;
	}
	.item .footer:last-of-type {
		padding-bottom: 0px;
	}

	@media (max-width: 1550px) {
		.item-grid {
			grid-template-columns: repeat(10, 1fr);
		}
	}
	@media (max-width: 1300px) {
		.item-grid {
			grid-template-columns: repeat(5, 1fr);
		}
	}
	@media (max-width: 1000px) {
		.item-grid {
			grid-template-columns: repeat(4, 1fr);
		}
	}
</style>
